import React from 'react'
import { Pill } from './pill'

// Whether a connection that is switched on sends anyone to it. Sign-in finds
// connections by the email's domain, so one with no domain bound is offered
// to nobody -- unless it is a public sign-in method (providers.public), a
// button on every sign-in screen. This is auth's provider-link rule since
// audit B3 ("a connection with no domain and no flag is simply not offered to
// anyone until a domain is bound"), and "bound" means what it means there:
// any live binding (domain_bindings), whatever the domain's verification
// state. The reads report it as the connection's bound domains (provider
// domains), providerBindingCount or boundDomainCount -- every organization's,
// not just the one a page is about.
export const NOT_IN_USE_REASON = 'Nobody is sent here: no domain is bound to it.'

// While a connection's test list has addresses (testers: how many), only
// those people are sent to it (core routing): its status says so, in amber,
// never the green of a connection live for everyone. `section` is where the
// pill leads (the Testing section).
export const TESTING_REASON = 'Only the people on its test list are sent here. Clear the list to make it live for everyone at its domains.'
export const TESTING_READY_REASON = 'Switched off. When it is switched on, only the people on its test list are sent here; clear the list to make it live for everyone at its domains.'

const people = n => (n === 1 ? '1 person' : `${n} people`)

// active: switched on. isPublic: providers.public (true / false; anything
// else is unknown). bound: how many domains are bound to it (a number;
// anything else is unknown). A read that doesn't carry the flag or the count
// never claims "Not in use". testers: the test list's length (0 or unknown:
// no list).
export const connectionUse = ({ active, isPublic, bound, testers }) => {
  const testing = typeof testers === 'number' && testers > 0
  if (!active) {
    if (testing) return { key: 'inactive-testing', label: `Inactive · Testing ready (${testers})`, tone: 'info', reason: TESTING_READY_REASON, section: 'testing' }
    return { key: 'inactive', label: 'Inactive', tone: null }
  }
  if (isPublic === false && bound === 0) return { key: 'not-in-use', label: 'Not in use', tone: null, reason: NOT_IN_USE_REASON }
  if (testing) return { key: 'testing', label: `Active · Testing (${people(testers)})`, tone: 'warn', reason: TESTING_REASON, section: 'testing' }
  return { key: 'active', label: 'Active', tone: 'ok' }
}

// Whether a provider row is a connection. A platform sign-in method's
// internal row (auth's `signinMethod`; a `public` row from an auth before
// sign-in methods) is not: it is managed on the Sign-in methods page, auth
// refuses it in every connection path (bindings, organization links, the
// CRUD), so the connection pages neither list it nor offer it (design
// edxr-workspace/.superpowers/signin-methods §1.2, §5).
export const isConnection = row => Boolean(row) && !row.signinMethod && !row.public

// The status pill: green Active, amber Active · Testing, blue Inactive ·
// Testing ready, grey Not in use, grey Inactive. A status that leads to a
// section is a button there (onGo) or a link (to), with its reason as the
// hover text.
export const ConnectionUsePill = ({ use, onGo, to, LinkComponent }) => {
  const pill = <Pill tone={use.tone} title={use.reason}>{use.label}</Pill>
  if (use.section && onGo) {
    return <button type="button" className="sw-pill-button" title={use.reason} onClick={() => onGo(use.section)}>{pill}<span className="visually-hidden"> (go to Testing)</span></button>
  }
  if (use.section && to && LinkComponent) {
    return <LinkComponent className="sw-pill-button" to={to} title={use.reason}>{pill}</LinkComponent>
  }
  return pill
}
